import {AuthenticationMethodType} from "./AuthenticationMethodType";
import {z as zod} from "zod";

export type SignInDeepLinkHints =
    | {method: AuthenticationMethodType.otpViaSms; phoneNumber?: string}
    | {method: AuthenticationMethodType.otpViaEmail; email?: string}
    | {method?: undefined};

const indianPhoneNumberPattern = /^(?:\+91|91)?([0-9]{10})$/;

function getSingleParameter(searchParams: URLSearchParams, name: string): string | undefined {
    const values = searchParams.getAll(name);
    return values.length === 1 ? values[0] : undefined;
}

/**
 * Parse raw URL query parameters before a router can coerce numeric identifiers.
 * These hints only choose and prefill a form; they never authenticate a user.
 * Encode a phone number's leading + as %2B (URLSearchParams does this for callers).
 */
export function parseSignInDeepLinkHints(searchParams: URLSearchParams): SignInDeepLinkHints {
    const method = getSingleParameter(searchParams, "method");

    if (method === AuthenticationMethodType.otpViaSms) {
        const phoneNumber = getSingleParameter(searchParams, "phoneNumber");
        const normalizedPhoneNumber = phoneNumber?.match(indianPhoneNumberPattern)?.[1];
        return normalizedPhoneNumber === undefined
            ? {method}
            : {method, phoneNumber: normalizedPhoneNumber};
    }

    if (method === AuthenticationMethodType.otpViaEmail) {
        const email = getSingleParameter(searchParams, "email");
        const parsedEmail = zod.email().safeParse(email);
        return parsedEmail.success
            ? {method, email: parsedEmail.data}
            : {method};
    }

    return {};
}

/** Revalidate hints from the authorization request before exposing them to the login UI. */
export function parseOidcSignInDeepLinkHints(params: Record<string, string>): SignInDeepLinkHints {
    return parseSignInDeepLinkHints(new URLSearchParams({
        method: params.login_method ?? "",
        phoneNumber: params.login_hint ?? "",
        email: params.login_hint ?? "",
    }));
}
